import {
  NOTICE_PLACEHOLDERS,
  NOTICE_DEFAULTS,
  renderNotice,
  type NoticeWordingView,
} from '@cyberscore/contracts';
import { RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { useNoticeWording, useResetWording, useSetWording } from './api';

const SAMPLE = { business: 'Kinta Precision Sdn Bhd', author: 'Aisyah Rahman', count: '3 answers' };

/** The insurer's own wording for its email notices (task 65). Tenant admins change it. * `embedded`: inside Tenant 360's tab (task 102), under a smaller heading.
 */
export function NoticesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const list = useNoticeWording();
  if (list.isPending) return <div className="empty">Loading…</div>;
  if (list.isError)
    return (
      <div className="empty">
        <strong>Could not load the notices</strong>
        {list.error.message}
      </div>
    );
  return (
    <>
      <div className={`page-head${embedded ? ' embedded' : ''}`}>
        {embedded ? <h2>Email notices</h2> : <h1>Email notices</h1>}
        <p>
          What people are emailed when something happens in an assessment. Notices never include what people
          wrote, only that there is something to read, with a link to it. Use{' '}
          {NOTICE_PLACEHOLDERS.map((p, i) => (
            <span key={p}>
              {i ? ', ' : ''}
              <code>{`{${p}}`}</code>
            </span>
          ))}{' '}
          for names and numbers.
        </p>
      </div>
      <div className="stack">
        {list.data.map((w) => (
          <NoticeEditor key={`${w.key}:${w.subject}:${w.body}`} w={w} />
        ))}
      </div>
    </>
  );
}

function NoticeEditor({ w }: { w: NoticeWordingView }) {
  const { tenant } = useSession();
  const set = useSetWording();
  const reset = useResetWording();
  const toast = useToast();
  const [subject, setSubject] = useState(w.subject);
  const [body, setBody] = useState(w.body);
  const [error, setError] = useState<string | null>(null);
  const changed = subject !== w.subject || body !== w.body;
  const preview = renderNotice({ subject, body }, { ...SAMPLE, insurer: tenant?.name ?? 'Your company' });
  const d = NOTICE_DEFAULTS[w.key];
  return (
    <section className="card pad notice-editor" aria-labelledby={`notice-${w.key}`}>
      <div className="row-between">
        <h2 id={`notice-${w.key}`} className="section-title">{w.label}</h2>
        <span className={`pill ${w.default ? 'neutral' : 'ok'}`}>{w.default ? 'Standard wording' : 'Your wording'}</span>
      </div>
      <p className="muted small">{d.when}</p>
      <form
        className="stack-sm"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          try {
            await set.mutateAsync({ key: w.key, body: { subject: subject.trim(), body: body.trim() } });
            toast('Saved', 'ok');
          } catch (err) {
            setError(err instanceof ApiError ? (err.issues[0]?.message ?? err.message) : 'Could not save');
          }
        }}
      >
        <label className="small" htmlFor={`subject-${w.key}`}>Subject</label>
        <input id={`subject-${w.key}`} className="input" maxLength={150} value={subject} onChange={(e) => setSubject(e.target.value)} />
        <label className="small" htmlFor={`body-${w.key}`}>Message</label>
        <textarea id={`body-${w.key}`} className="input" rows={3} maxLength={1000} value={body} onChange={(e) => setBody(e.target.value)} />
        <div className="notice-preview small">
          <div className="muted">Preview</div>
          <b>{preview.subject}</b>
          <p>Hello Aisyah,</p>
          <p>{preview.body}</p>
          <p className="muted">[Open the conversation]</p>
        </div>
        {error && <div className="notice" role="alert">{error}</div>}
        <div className="row gap">
          <button type="submit" className="btn btn-primary btn-sm" disabled={!changed || !subject.trim() || !body.trim() || set.isPending}>
            Save
          </button>
          {!w.default && (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={reset.isPending}
              onClick={async () => {
                try {
                  await reset.mutateAsync(w.key);
                  toast('Back to the standard wording', 'ok');
                } catch (err) {
                  toast(err instanceof Error ? err.message : 'Could not reset', 'bad');
                }
              }}
            >
              <RotateCcw size={13} /> Use the standard wording
            </button>
          )}
        </div>
      </form>
    </section>
  ); // prettier-ignore
}
